import React from 'react';
import { RiskAnalysisDTO } from '../../models/companyModels';
import { 
  getRiskScoreColor,
  getConfidenceColor,
  getImpactLevelColor,
  formatRiskScore,
  formatConfidence
} from '../../models/riskAnalysisModels';

const RiskAnalysisCard = ({ riskAnalysis, className = '' }) => {
  if (!riskAnalysis) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-2">📊</div>
          <p>No risk analysis available</p>
        </div>
      </div>
    );
  }

  const analysis = new RiskAnalysisDTO(riskAnalysis);
  const overallRiskColor = analysis.getOverallRiskColor();
  const newsSentimentColor = analysis.getNewsSentimentColor();

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Risk Analysis</h3>
          <p className="text-sm text-gray-500">
            Last updated: {analysis.getFormattedAnalysisDate()}
          </p>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${overallRiskColor.bg} ${overallRiskColor.text}`}>
          {analysis.getOverallRiskLevel()}
        </div>
      </div>

      {/* Overall Risk Score */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Overall Risk Score</span>
          <span className={`text-lg font-bold ${overallRiskColor.text}`}>
            {formatRiskScore(analysis.overallRiskScore)}/100
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${overallRiskColor.bg.replace('bg-', 'bg-').replace('-50', '')}`}
            style={{ width: `${analysis.overallRiskScore}%` }}
          ></div>
        </div>
      </div>

      {/* Risk Score Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Supply Chain Risk */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Supply Chain</span>
            <span className={`text-sm font-semibold ${getRiskScoreColor(analysis.supplyChainRiskScore).text}`}>
              {formatRiskScore(analysis.supplyChainRiskScore)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full ${getRiskScoreColor(analysis.supplyChainRiskScore).bg.replace('bg-', 'bg-').replace('-50', '')}`}
              style={{ width: `${analysis.supplyChainRiskScore}%` }}
            ></div>
          </div>
        </div>

        {/* Industry Risk */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Industry</span>
            <span className={`text-sm font-semibold ${getRiskScoreColor(analysis.industryRiskScore).text}`}>
              {formatRiskScore(analysis.industryRiskScore)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full ${getRiskScoreColor(analysis.industryRiskScore).bg.replace('bg-', 'bg-').replace('-50', '')}`}
              style={{ width: `${analysis.industryRiskScore}%` }}
            ></div>
          </div>
        </div>

        {/* News Sentiment */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">News Sentiment</span>
            <span className={`text-sm font-semibold ${newsSentimentColor.text}`}>
              {formatRiskScore(analysis.newsSentimentScore)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full ${newsSentimentColor.bg.replace('bg-', 'bg-').replace('-50', '')}`}
              style={{ width: `${analysis.newsSentimentScore}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* News Analysis Summary */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-2">News Analysis</h4>
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span className="text-gray-600">Positive: {analysis.positiveNewsCount}</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            <span className="text-gray-600">Negative: {analysis.negativeNewsCount}</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
            <span className="text-gray-600">Neutral: {analysis.neutralNewsCount}</span>
          </div>
          <div className="text-gray-500">
            Total: {analysis.analyzedNewsCount} articles
          </div>
        </div>
      </div>

      {/* Analysis Quality */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center">
          <span className="text-gray-600 mr-2">Confidence:</span>
          <span className={`font-medium ${getConfidenceColor(analysis.analysisConfidence)}`}>
            {formatConfidence(analysis.analysisConfidence)}%
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-gray-600 mr-2">Data Quality:</span>
          <span className={`font-medium ${getConfidenceColor(analysis.dataQualityScore)}`}>
            {formatConfidence(analysis.dataQualityScore)}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default RiskAnalysisCard;
